import type { MapImage, Point } from '../types/app.js';
import { MAP_IMAGE_ASSET_ID_PATTERN, isMapImageDataUrl } from '../shared/map-image.js';
export { MAX_MAP_IMAGES } from '../shared/map-image.js';

export function cloneMapImage(image: MapImage): MapImage {
  const cloned: MapImage = {
    id: image.id,
    assetId: image.assetId,
    points: imagePointTuple(image.points),
    opacity: image.opacity,
  };
  if (image.group) cloned.group = [...image.group];
  if (image.locked) cloned.locked = true;
  if (image.hidden === true) cloned.hidden = true;
  if (image.name) cloned.name = image.name;
  if (image.sourceMapId) cloned.sourceMapId = image.sourceMapId;
  if (image.dataUrl) cloned.dataUrl = image.dataUrl;
  return cloned;
}

function imagePointTuple(points: readonly Point[]): [Point, Point, Point, Point] {
  // SAFETY: The fixed four-item source tuple keeps all four entries present.
  return [points[0], points[1], points[2], points[3]].map((point) => ({ ...point })) as [Point, Point, Point, Point];
}

export function isValidMapImagePoint(point: Point): boolean {
  return Number.isFinite(point.lat)
    && Number.isFinite(point.lng)
    && Math.abs(point.lat) <= 90
    && Math.abs(point.lng) <= 540;
}

export function normalizeMapImage(
  image: Partial<MapImage>,
  generateImageId: () => string,
): MapImage | null {
  if (!image.assetId || !MAP_IMAGE_ASSET_ID_PATTERN.test(image.assetId)) return null;
  if (image.dataUrl !== undefined && !isMapImageDataUrl(image.dataUrl)) return null;
  if (!Array.isArray(image.points) || image.points.length !== 4 || !image.points.every(isValidMapImagePoint)) return null;
  const normalized: MapImage = {
    id: image.id || generateImageId(),
    assetId: image.assetId,
    points: imagePointTuple(image.points),
    opacity: image.opacity !== undefined && Number.isFinite(image.opacity)
      ? Math.max(0.1, Math.min(1, image.opacity))
      : 1,
  };
  if (image.group) normalized.group = image.group.slice(0, 6).map(name => name.slice(0, 80));
  if (image.locked) normalized.locked = true;
  if (image.hidden === true) normalized.hidden = true;
  if (image.name) normalized.name = image.name.slice(0, 120);
  if (image.sourceMapId) normalized.sourceMapId = image.sourceMapId;
  if (image.dataUrl) normalized.dataUrl = image.dataUrl;
  return normalized;
}

export function mapImageUrl(image: MapImage, mapId?: string | null): string | null {
  if (image.dataUrl) return image.dataUrl;
  const source = image.sourceMapId || mapId;
  return source ? `/api/maps/${encodeURIComponent(source)}/images/${encodeURIComponent(image.assetId)}` : null;
}
